import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";

interface LoadingSpinnerProps {
  /**
   * Size of the spinner
   * @default "md"
   */
  size?: "sm" | "md" | "lg" | "xl";
  /**
   * Text to display below spinner
   */
  text?: string;
  /**
   * Whether to center in full screen
   * @default true
   */
  fullScreen?: boolean;
  /**
   * Custom className for the container
   */
  className?: string;
  /**
   * Use Lucide icon instead of custom spinner
   * @default false
   */
  useLucideIcon?: boolean;
}

const sizeClasses = {
  sm: "w-4 h-4 border-2",
  md: "w-8 h-8 border-2",
  lg: "w-12 h-12 border-4",
  xl: "w-16 h-16 border-4",
};

const lucideSizes = {
  sm: "h-4 w-4",
  md: "h-8 w-8",
  lg: "h-12 w-12",
  xl: "h-16 w-16",
};

/**
 * Loading spinner component
 *
 * Flexible spinner that can be used for full-screen loading states
 * or inline within components.
 *
 * @example
 * // Full screen loading
 * <LoadingSpinner text="Cargando datos..." />
 *
 * @example
 * // Inline small spinner
 * <LoadingSpinner size="sm" fullScreen={false} />
 *
 * @example
 * // Using Lucide icon
 * <LoadingSpinner useLucideIcon text="Procesando..." />
 */
const LoadingSpinner: React.FC<LoadingSpinnerProps> = ({
  size = "md",
  text = "Cargando...",
  fullScreen = true,
  className,
  useLucideIcon = false,
}) => {
  const spinner = useLucideIcon ? (
    <Loader2 className={cn("animate-spin text-primary", lucideSizes[size])} />
  ) : (
    <div
      className={cn(
        "border-primary border-t-transparent rounded-full animate-spin",
        sizeClasses[size]
      )}
    />
  );

  if (fullScreen) {
    return (
      <div className={cn("flex items-center justify-center min-h-screen", className)}>
        <div className="text-center">
          {spinner}
          {text && (
            <p className="text-sm text-muted-foreground mt-4">{text}</p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn("flex items-center gap-2", className)}>
      {spinner}
      {text && <span className="text-sm text-muted-foreground">{text}</span>}
    </div>
  );
};

export default LoadingSpinner;